import { useState, useCallback, useEffect } from 'react';

const SCROLL_THRESHOLD = 10;

export const drawerRegistry = {
  mainDrawerSetVisible: null as ((visible: boolean) => void) | null,

  registerMainDrawer(setter: (visible: boolean) => void) {
    this.mainDrawerSetVisible = setter;
  },

  unregisterMainDrawer() {
    this.mainDrawerSetVisible = null;
  },

  closeMainDrawer() {
    if (this.mainDrawerSetVisible) {
      this.mainDrawerSetVisible(false);
    }
  },
};

interface ScrollMovementProps {
  containerSelector?: string;
}

export const useScrollMovement = (
  isMobile: boolean,
  { containerSelector = '.main-container' }: ScrollMovementProps = {},
) => {
  const [drawerVisible, setDrawerVisible] = useState(isMobile);
  const [lastTouchY, setLastTouchY] = useState(0);
  const [lastScrollY, setLastScrollY] = useState(0);

  useEffect(() => {
    drawerRegistry.registerMainDrawer(setDrawerVisible);

    return () => {
      drawerRegistry.unregisterMainDrawer();
    };
  }, []);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    setLastTouchY(e.touches[0].clientY);
  }, []);

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isMobile) return;
      const currentTouchY = e.touches[0].clientY;
      const touchDiff = currentTouchY - lastTouchY;
      const atBottom = isAtBottom(containerSelector);
      if (atBottom) {
        setDrawerVisible(true);
      } else if (touchDiff < -SCROLL_THRESHOLD) {
        setDrawerVisible(false);
      } else if (touchDiff > SCROLL_THRESHOLD) {
        setDrawerVisible(true);
      }
      setLastTouchY(currentTouchY);
    },
    [isMobile, lastTouchY, containerSelector],
  );

  const handleScroll = useCallback(() => {
    if (!isMobile) return;
    const currentScrollY = window.scrollY;
    const scrollDiff = currentScrollY - lastScrollY;
    const atBottom = isAtBottom(containerSelector);
    if (atBottom) {
      setDrawerVisible(true);
    } else if (scrollDiff > SCROLL_THRESHOLD) {
      setDrawerVisible(false);
    } else if (scrollDiff < -SCROLL_THRESHOLD) {
      setDrawerVisible(true);
    }
    setLastScrollY(currentScrollY);
  }, [isMobile, lastScrollY, containerSelector]);

  const handleWheelEvent = useCallback(
    (e: WheelEvent) => {
      const atBottom = isAtBottom(containerSelector);
      if (atBottom) {
        setDrawerVisible(true);
      } else if (e.deltaY > 0) {
        setDrawerVisible(false);
      } else if (e.deltaY < 0) {
        setDrawerVisible(true);
      }
    },
    [containerSelector],
  );

  useEffect(() => {
    if (isMobile) {
      document.addEventListener('touchstart', handleTouchStart, { passive: true });
      document.addEventListener('touchmove', handleTouchMove, { passive: true });
      window.addEventListener('wheel', handleWheelEvent, { passive: true });
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => {
        document.removeEventListener('touchstart', handleTouchStart);
        document.removeEventListener('touchmove', handleTouchMove);
        window.removeEventListener('wheel', handleWheelEvent);
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isMobile, handleTouchStart, handleTouchMove, handleWheelEvent, handleScroll]);

  useEffect(() => {
    if (!isMobile) {
      setDrawerVisible(false);
      return;
    }

    const container = document.querySelector(containerSelector);
    if (!container) return;

    const onScrollStop = () => {
      if (isAtBottom(containerSelector)) {
        setDrawerVisible(true);
      }
    };

    let scrollingTimer = null;
    const handleScrollEvent = () => {
      if (scrollingTimer) clearTimeout(scrollingTimer);
      scrollingTimer = setTimeout(onScrollStop, 100);
    };

    container.addEventListener('scroll', handleScrollEvent, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScrollEvent);
      if (scrollingTimer) clearTimeout(scrollingTimer);
    };
  }, [isMobile, containerSelector]);

  useEffect(() => {
    setDrawerVisible(isMobile);
  }, [isMobile]);

  return {
    drawerVisible,
    setDrawerVisible,
  };
};

const isAtBottom = (containerSelector: string) => {
  const container = document.querySelector(containerSelector);
  if (!container) return false;
  const scrollHeight = container.scrollHeight;
  const scrollTop = container.scrollTop;
  const clientHeight = container.clientHeight;
  const buffer = 10;
  return scrollHeight - Math.round(scrollTop + clientHeight) <= buffer;
};
